import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Group, Paper, Space, Stack, Text, Title } from '@mantine/core';
import { NotificationToggle } from '../components/NotificationToggle';
import { IconBellRinging, IconBolt, IconCalendarPlus, IconInfoCircle, IconUsers } from '@tabler/icons-react';
import { NavigationWrapper } from '../components/navigation/NavigationWrapper';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useLoading } from '../context/LoadingContext';
import { primaryColor } from '../constants/theme';
import { PageRoutes } from '../constants/routes';
import {
  getAllNotificationPreferences,
  toggleNotificationPreference,
} from '../services/NotificationPreferencesService';
import { NotificationPreferenceCategory } from '../types/NotificationPreferenceModels';

export const NotificationSettings = () => {
  const { profile, scrollAreaHeight } = useApp();
  const navigate = useNavigate();
  const { setIsLoading } = useLoading();
  const [isPromptToAddTimeSlotsEnabled, setIsPromptToAddTimeSlotsEnabled] =
    useState(true);
  const [isFetchingPreference, setIsFetchingPreference] = useState(true);
  const [isUpdatingPreference, setIsUpdatingPreference] = useState(false);
  const [isDemandNotificationsEnabled, setIsDemandNotificationsEnabled] =
    useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isProfessional = useMemo(
    () => Boolean(profile?.isProfessional),
    [profile?.isProfessional]
  );

  useEffect(() => {
    const fetchPreferences = async () => {
      setIsFetchingPreference(true);
      setErrorMessage(null);

      try {
        const preferences = await getAllNotificationPreferences();
        setIsPromptToAddTimeSlotsEnabled(
          preferences[NotificationPreferenceCategory.PromptAvailabilityPreferenceCategory] ?? true
        );
        setIsDemandNotificationsEnabled(
          preferences[NotificationPreferenceCategory.AvailabilityRequestedPreferenceCategory] ?? true
        );
      } catch {
        setErrorMessage(
          'Unable to load notification settings right now. Please try again shortly.'
        );
      } finally {
        setIsFetchingPreference(false);
      }
    };

    fetchPreferences();
  }, []);

  useEffect(() => {
    setIsLoading(isFetchingPreference || isUpdatingPreference);

    return () => {
      setIsLoading(false);
    };
  }, [isFetchingPreference, isUpdatingPreference, setIsLoading]);

  const handleToggleDemandNotifications = async () => {
    const previousState = isDemandNotificationsEnabled;
    const nextState = !previousState;

    setIsDemandNotificationsEnabled(nextState);
    setIsUpdatingPreference(true);
    setErrorMessage(null);

    try {
      await toggleNotificationPreference(NotificationPreferenceCategory.AvailabilityRequestedPreferenceCategory, nextState);
    } catch {
      setIsDemandNotificationsEnabled(previousState);
      setErrorMessage(
        'Unable to save your notification setting. Please try again.'
      );
    } finally {
      setIsUpdatingPreference(false);
    }
  };

  const handleTogglePromptToAddTimeSlots = async () => {
    const previousState = isPromptToAddTimeSlotsEnabled;
    const nextState = !previousState;

    setIsPromptToAddTimeSlotsEnabled(nextState);
    setIsUpdatingPreference(true);
    setErrorMessage(null);

    try {
      await toggleNotificationPreference(NotificationPreferenceCategory.PromptAvailabilityPreferenceCategory, nextState);
    } catch {
      setIsPromptToAddTimeSlotsEnabled(previousState);
      setErrorMessage(
        'Unable to save your notification setting. Please try again.'
      );
    } finally {
      setIsUpdatingPreference(false);
    }
  };

  return (
    <NavigationWrapper>
      <Stack align="center">
          <Paper w={'100%'} maw={1200} p='md' h={scrollAreaHeight}>
              <Title order={3} fw="normal">Notification Settings</Title>
              <Space h="md" />
              <Stack gap={10}>
                {isProfessional ? (
                <NotificationToggle
                  icon={<IconCalendarPlus size={18} color={primaryColor} />}
                  checked={isPromptToAddTimeSlotsEnabled}
                  loading={isFetchingPreference || isUpdatingPreference}
                  onChange={handleTogglePromptToAddTimeSlots}
                  label="Prompt to Add Time Slots"
                  description="Receive a reminder notification to add time slots when you have not added any availability in the last 14 days."
                  testId="notification-toggle-availability"
                />
              ) : null}

              {isProfessional ? (
                <NotificationToggle
                  icon={<IconBolt size={18} color={primaryColor} />}
                  checked={isDemandNotificationsEnabled}
                  loading={isFetchingPreference || isUpdatingPreference}
                  onChange={handleToggleDemandNotifications}
                  label="Demand Notifications"
                  description="If you sell out or haven't loaded your availability, we will notify you when fans want to talk. Capped at 5 notifications per day."
                  testId="notification-toggle-demand"
                />
              ) : null}

              <Paper p="md" withBorder data-testid="follower-notifications-section">
                <Group gap="xs" mb="xs">
                  <IconUsers size={18} color={primaryColor} />
                  <Title order={5} fw="500">Following Notifications</Title>
                </Group>
                <Text size="sm" c="dimmed" ta="left" mb="sm">
                  You can choose to be notified when creators you follow add new availability.
                  Manage per-creator notification preferences on your Following page.
                </Text>
                <Button
                  fullWidth
                  variant="outline"
                  color={primaryColor}
                  onClick={() => navigate(PageRoutes.FOLLOWING)}
                  data-testid="follower-notifications-link"
                >
                  Go to Following
                </Button>
              </Paper>
            </Stack>
          </Paper>


            {errorMessage && (
              <Alert variant="light" color="red" icon={<IconInfoCircle size={16} />}>
                {errorMessage}
              </Alert>
            )}


      </Stack>
    </NavigationWrapper>
  );
};
